import React from 'react';
import Icon from '@/shared/components/Icon';
import HorizontalCategoryChart from '@/shared/charts/HorizontalCategoryChart';
import BiomarkerDetailChart from '@/shared/charts/BiomarkerDetailChart';
import SectionContainer from '@/shared/components/SectionContainer';
import {HEALTH_CATEGORIES, getCategoryStats, filterTestsByCategory} from '@/modules/patient-dashboard/utils/healthCategories';
import {useTestsState} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';
import SectionHeader from '@/shared/components/SectionHeader';
import Card from '@/shared/components/Card';

interface HealthOverviewProps {
    selectedCategory: string;
    onCategoryChange: (category: string) => void;
    onBiomarkerSelect?: (biomarkerName: string) => void;
    selectedBiomarkerName?: string | null;
}

const HealthOverview: React.FC<HealthOverviewProps> = ({
    selectedCategory,
    onCategoryChange,
    onBiomarkerSelect,
    selectedBiomarkerName,
}) => {
    const testsState = useTestsState();
    const {rules} = useDisplayRules();

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData} = useDashboardData();

    const isLoading = testsState.loading;

    // Get all results for filtering
    const allResults = React.useMemo(() => {
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                })) || []
        );
    }, [currentPatientData]);

    // Get categories that have available tests - sorted by score descending to match chart order
    const availableCategories = React.useMemo(() => {
        const categoryStats = getCategoryStats(allResults);
        return categoryStats
            .filter((category) => category.availableTests > 0)
            .sort((a, b) => Math.round(b.averageValue) - Math.round(a.averageValue)) // Sort by score descending
            .map((category) => [category.key, HEALTH_CATEGORIES[category.key]] as const);
    }, [allResults]);

    const currentCategoryIndex = availableCategories.findIndex(([key]) => key === selectedCategory);
    const currentCategory = HEALTH_CATEGORIES[selectedCategory];

    const handlePrevCategory = () => {
        if (availableCategories.length === 0) return;
        const prevIndex = currentCategoryIndex > 0 ? currentCategoryIndex - 1 : availableCategories.length - 1;
        onCategoryChange(availableCategories[prevIndex][0]);
    };

    const handleNextCategory = () => {
        if (availableCategories.length === 0) return;
        const nextIndex = currentCategoryIndex < availableCategories.length - 1 ? currentCategoryIndex + 1 : 0;
        onCategoryChange(availableCategories[nextIndex][0]);
    };

    // Count of biomarkers in the selected category — shown in the right
    // panel header to match the design's "🛡️ N Biomarkers" affordance.
    const currentCategoryBiomarkerCount = React.useMemo(() => {
        // Count DISTINCT reporting biomarkers (by test_name) in the category —
        // this mirrors exactly what the detail panel renders (it groups the same
        // filtered results by test_name). The old `availableTests` counted raw
        // result ROWS (one per biomarker per blood draw), which over-counted
        // (e.g. "96 Biomarkers" for 6 distinct heart markers across 16 draws).
        const inCategory = filterTestsByCategory(allResults, selectedCategory);
        return new Set(inCategory.map((r: {test_name: string}) => r.test_name)).size;
    }, [allResults, selectedCategory]);

    return (
        <section>
            <div className="flex justify-between">
                <SectionHeader title="Health Overview" />
                <p className="inline-flex mb-2 text-sm text-mld-muted-text items-end">
                    An overall view of your whole body health and the markers that affect it
                </p>
            </div>

            <Card
                variant="elevated"
                rounded="2xl"
                className='p-6'>
                <div className="grid grid-cols-1 lg:grid-cols-9 gap-4">
                {/* Left Panel: Category List */}
                <div className="lg:col-span-4 bg-white rounded-2xl border border-mld-panel-border overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200">
                        <h3 className="text-2xl font-medium text-mld-text-primary">By Category</h3>
                    </div>
                    <div>
                        <HorizontalCategoryChart
                            selectedCategory={selectedCategory}
                            onCategorySelect={onCategoryChange}
                        />
                    </div>
                </div>

                {/* Right Panel: Selected Category Biomarkers with Inline Trends */}
                <div className="lg:col-span-5 bg-white rounded-2xl border border-mld-panel-border overflow-hidden">
                    {/* Category Header — same px-5 py-4 height as By Category; title
                        left-aligned next to the prev arrow (matches source). */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handlePrevCategory}
                                aria-label="Previous category"
                                className="w-6 h-6 rounded-full flex items-center justify-center text-mld-text-primary hover:text-mld-primary hover:bg-gray-100 transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                            </button>
                            <h3 className="text-2xl font-medium text-mld-text-primary">
                                {isLoading ? 'Loading...' : currentCategory?.name || 'Health Category'}
                            </h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-1.5 text-xs text-mld-text-primary">
                                <Icon name="shield-check" className="h-3.5 w-3.5" />
                                <span>{currentCategoryBiomarkerCount} Biomarkers</span>
                            </div>
                            <button
                                onClick={handleNextCategory}
                                aria-label="Next category"
                                className="w-6 h-6 rounded-full flex items-center justify-center text-mld-text-primary hover:text-mld-primary hover:bg-gray-100 transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Biomarker Details with Inline Trend Charts */}
                    <div className="max-h-[242px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-mld-panel-border scrollbar-track-transparent">
                        <BiomarkerDetailChart
                            selectedCategory={selectedCategory}
                            onBiomarkerSelect={onBiomarkerSelect}
                            selectedBiomarkerName={selectedBiomarkerName}
                            rules={rules}
                        />
                    </div>
                </div>
            </div>
            </Card>
        </section>
    );
};

export default HealthOverview;
